import React from "react";
import { 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle2, 
  HeartHandshake, 
  Stethoscope, 
  MessageCircle, 
  ArrowRight,
  Info
} from "lucide-react";
import { getWhatsAppChatUrl } from "../../services/coreServicesClient";

export const TRIAGE_LEVEL_DEFINITIONS = {
  0: {
    level: 0,
    title: "Nivel 0: Saludable & Resiliente",
    shortBadge: "Nivel 0: Óptimo",
    color: "#10B981",
    bg: "bg-emerald-50/70",
    border: "border-emerald-200",
    text: "text-emerald-800",
    icon: CheckCircle2,
    specialist: "Programa de Control Continuo (PCC)",
    specialistRole: "Salud Preventiva Autónoma",
    explanation: "Tus biomarcadores clínicos, tensión arterial, grasa visceral y nivel de estrés se encuentran dentro de los rangos protectores óptimos.",
    action: "Mantén tus rutinas activas, realiza tu escaneo facial semanal y continúa hidratándote adecuadamente.",
    ctaText: "Ver Retos de Bienestar",
    ctaLink: "/measure"
  },
  1: {
    level: 1,
    title: "Nivel 1: Monitoreo Preventivo (IHO)",
    shortBadge: "Nivel 1: Prevención Activa",
    color: "#F59E0B",
    bg: "bg-amber-50/70",
    border: "border-amber-200",
    text: "text-amber-800",
    icon: HeartHandshake,
    specialist: "Oficial de Salud (IHO)",
    specialistRole: "Acompañamiento en Hábitos & Estilo de Vida",
    explanation: "Identificamos 1 factor leve a optimizar (como deuda de sueño, sedentarismo laboral o estrés reactivo temporal).",
    action: "Tienes habilitado el acompañamiento directo de tu Oficial de Salud asignado para calibrar tus hábitos sin fármacos.",
    ctaText: "Chatear con mi Oficial de Salud",
    isWhatsApp: true
  },
  2: {
    level: 2,
    title: "Nivel 2: Riesgo Moderado Concurrente",
    shortBadge: "Nivel 2: Sugerencia Médica",
    color: "#F97316",
    bg: "bg-orange-50/70",
    border: "border-orange-200",
    text: "text-orange-800",
    icon: Stethoscope,
    specialist: "Médico de Familia / Medicina General",
    specialistRole: "Evaluación Médica Preventiva",
    explanation: "Se detectó la concurrencia de 2 o más factores de riesgo (ej. tensión elevada, grasa visceral o perímetro abdominal limítrofe).",
    action: "Te sugerimos agendar una consulta médica de orientación para corroborar tus biomarcadores y recibir una guía clínica individualizada.",
    ctaText: "Consultar con Médico de Familia",
    isWhatsApp: true
  },
  3: {
    level: 3,
    title: "Nivel 3: Alerta Clínica Prioritaria",
    shortBadge: "Nivel 3: Atención Urgente",
    color: "#EF4444",
    bg: "bg-rose-50/70",
    border: "border-rose-200",
    text: "text-rose-800",
    icon: AlertTriangle,
    specialist: "Equipo Médico Clínico WE360",
    specialistRole: "Recomendación de Valoración Inmediata",
    explanation: "Se han identificado cifras críticas en tensión arterial o descompensación clínica evidente que ameritan revisión inmediata.",
    action: "Por tu seguridad, comunícate con el equipo médico de urgencias o asiste al centro de salud más cercano para una valoración presencial.",
    ctaText: "Contacto Médico Urgente",
    isWhatsApp: true
  }
};

/**
 * Calcula el nivel de triaje individual
 */
export function calcIndividualTriageLevel(profile = {}, latestMeasurement = {}, isDemo = false) {
  if (isDemo) {
    return { ...TRIAGE_LEVEL_DEFINITIONS[0], isEvaluated: true };
  }

  const p = profile || {};
  const vitals = latestMeasurement?.vitals || latestMeasurement?.fields || p.vitals || {};

  const pas = parseFloat(p.paSistolica || vitals.sbp || vitals.pas);
  const pad = parseFloat(p.paDiastolica || vitals.dbp || vitals.pad);
  const stress = parseFloat(vitals.stressIndex || vitals.si);
  const sueno = parseFloat(p.sueno || p.lifestyle?.sueno);
  const cintura = parseFloat(p.cintura || p.clinical_measurements?.cintura);
  const talla = parseFloat(p.talla || p.height || p.clinical_measurements?.talla);

  const hasAnyData = !isNaN(pas) || !isNaN(stress) || !isNaN(sueno) || !isNaN(cintura);
  if (!hasAnyData) {
    return {
      level: 0,
      title: "Calibración Inicial Pendiente",
      shortBadge: "En Calibración",
      color: "#64748B",
      bg: "bg-slate-50",
      border: "border-slate-200",
      text: "text-slate-700",
      icon: Info,
      specialist: "Asistente Preventivo WE360",
      specialistRole: "Guía de Bienestar",
      explanation: "Aún no has registrado mediciones suficientes para estratificar tu nivel de acompañamiento clínico.",
      action: "Realiza tu primer escaneo facial o registra tus datos somatométricos para conocer tu nivel de salud.",
      ctaText: "Comenzar Evaluación",
      ctaLink: "/measure",
      isEvaluated: false
    };
  }

  let level = 0;
  let riskFactorsCount = 0;

  // Nivel 3: Crisis o cifras críticas
  if ((pas >= 140) || (pad >= 90) || (stress > 650)) {
    return { ...TRIAGE_LEVEL_DEFINITIONS[3], isEvaluated: true };
  }

  // Factores individuales
  if (pas >= 130 || pad >= 85) riskFactorsCount++;
  if (cintura > 0 && talla > 0 && (cintura / talla > 0.50)) riskFactorsCount++;
  if (sueno > 0 && sueno < 7.0) riskFactorsCount++;
  if (stress > 350) riskFactorsCount++;

  if (riskFactorsCount >= 2) {
    level = 2;
  } else if (riskFactorsCount === 1) {
    level = 1;
  } else {
    level = 0;
  }

  return { ...TRIAGE_LEVEL_DEFINITIONS[level], isEvaluated: true };
}

export default function PersonalTriageCard({
  profile = {},
  latestMeasurement = {},
  isDemo = false,
  onNavigateToMeasurement,
  className = "",
}) {
  const triage = calcIndividualTriageLevel(profile, latestMeasurement, isDemo);
  const IconComponent = triage.icon;

  const handleCtaClick = () => {
    if (triage.isWhatsApp) {
      const msg = `Hola, soy ${profile.nombre || "usuario de WE360"}. Mi estado de salud en plataforma figura en ${triage.shortBadge}. Deseo consultar con mi profesional asignado.`;
      const url = getWhatsAppChatUrl(undefined, msg);
      window.open(url, "_blank");
    } else if (onNavigateToMeasurement) {
      onNavigateToMeasurement();
    }
  };

  return (
    <div
      className={`bg-white/80 backdrop-blur-md border border-slate-200/80 rounded-3xl p-6 shadow-sm transition-all ${className}`}
      data-testid="personal-triage-card"
    >
      {/* Cabecera del Triaje */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#00628F] bg-[#00628F]/10 px-2.5 py-0.5 rounded-md">
              Estratificación HI-OS
            </span>
            <span
              className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-md border flex items-center gap-1 ${triage.bg} ${triage.border} ${triage.text}`}
              data-testid="triage-badge"
            >
              <IconComponent size={11} /> {triage.shortBadge}
            </span>
          </div>
          <h3 className="text-xl font-extrabold text-slate-900 mt-1">
            Tu Nivel de Acompañamiento Clínico
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Clasificación oficial según el protocolo clínico de medicina preventiva de WE360.
          </p>
        </div>

        {/* Profesional Asignado */}
        <div className="flex items-center gap-3 bg-slate-50 border border-slate-200/80 rounded-2xl p-3 shrink-0">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${triage.color}15`, color: triage.color }}>
            <IconComponent size={20} />
          </div>
          <div>
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">
              Equipo Clínico Asignado
            </span>
            <h4 className="text-xs font-bold text-slate-900 leading-tight">
              {triage.specialist}
            </h4>
            <span className="text-[10px] text-slate-500 font-medium">
              {triage.specialistRole}
            </span>
          </div>
        </div>
      </div>

      {/* Escala Visual de los 4 Niveles */}
      <div className="mb-5">
        <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase mb-2">
          <span>Escala de Triaje de Salud (Niveles 0 a 3)</span>
          <span>Nivel Activo: {triage.level}</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[0, 1, 2, 3].map((lvl) => {
            const def = TRIAGE_LEVEL_DEFINITIONS[lvl];
            const isCurrent = triage.isEvaluated && triage.level === lvl;

            return (
              <div
                key={lvl}
                className={`p-2.5 rounded-xl border transition-all text-center ${
                  isCurrent
                    ? `${def.bg} ${def.border} ring-2 ring-offset-1`
                    : "bg-slate-50/60 border-slate-200/60 opacity-60"
                }`}
                style={isCurrent ? { ringColor: def.color } : {}}
              >
                <div className="flex items-center justify-center gap-1">
                  <span
                    className="w-2 h-2 rounded-full inline-block"
                    style={{ backgroundColor: def.color }}
                  />
                  <span className="text-xs font-black text-slate-800">
                    {`Nivel ${lvl}`}
                  </span>
                </div>
                <span className="text-[10px] text-slate-500 block truncate font-medium mt-0.5">
                  {lvl === 0 ? "Saludable" : lvl === 1 ? "IHO" : lvl === 2 ? "Médico" : "Alerta"}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detalle Clínico y Acción */}
      <div className={`p-4 rounded-2xl border ${triage.border} ${triage.bg} flex flex-col md:flex-row md:items-center justify-between gap-4`}>
        <div className="space-y-1 flex-1">
          <div className="flex items-center gap-2">
            <h4 className={`text-sm font-bold ${triage.text}`}>
              {triage.title}
            </h4>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            {triage.explanation}
          </p>
          <p className="text-xs font-medium text-slate-700 mt-1">
            <strong>Plan de acción:</strong> {triage.action}
          </p>
        </div>

        <button
          onClick={handleCtaClick}
          className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl shadow-sm hover:shadow transition-all flex items-center justify-center gap-2 shrink-0 cursor-pointer"
          data-testid="triage-action-btn"
        >
          {triage.isWhatsApp ? <MessageCircle size={14} className="text-emerald-400" /> : <ArrowRight size={14} />}
          {triage.ctaText}
        </button>
      </div>

      {/* Footer Informativo */}
      <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between text-[10px] text-slate-400 gap-2">
        <span className="flex items-center gap-1.5">
          <ShieldCheck size={12} className="text-emerald-600" /> Protocolo de Medicina Preventiva OIT C161 & AHA
        </span>
        <span>Asignación automática basada en tus últimas mediciones</span>
      </div>
    </div>
  );
}
